import { useCallback, useEffect } from "react";
import { useRouter } from "next/router";

import clsx from "clsx";

import ApiError from "api/Error";

import {
  SingleColumnSelect,
  MultiColumnSelect,
} from "modules/data-files/elements/Column-Select";

import * as flowConfig from "flows/impact-analysis-binary/config";

import { useGetCreate } from "./api";
import {
  useInputFile,
  useGetYDefinition,
  useGetTreatmentDefinition,
  useTimeColumn,
  useIDVariables,
} from "./store";

import SelectData from "./Select-Data";
import SetYDefinition from "./Set-Y";
import SetTreatment from "./Set-Treatment";

const CreateProject = () => {
  const router = useRouter();
  const data = router.query.data as string;
  const { push } = router;

  const [currentFile, setCurrentFile] = useInputFile();

  const yDefinition = useGetYDefinition();
  const treatment = useGetTreatmentDefinition();

  const [timestampColumn, setTimestampColumn] = useTimeColumn();
  const [idColumns, setIdColumns] = useIDVariables();

  const [createProject, submitStatus, error] = useGetCreate();

  useEffect(() => {
    if (data && data !== currentFile) {
      setCurrentFile(data);
    }
  }, [data, currentFile]);

  const create = useCallback(async () => {
    const resp = await createProject();
    if (resp?.success) {
      push(`/${flowConfig.FLOW_NAME}/${resp.project.id}`);
    }
  }, [createProject, push]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <SelectData />
      {currentFile && (
        <>
          <div className="mt-2 pt-2">
            <SetYDefinition />
          </div>
          <div className="mt-2 pt-2">
            <SetTreatment />
          </div>
          <div className="flex mt-2 pt-2">
            <div className="w-1/4">
              <label htmlFor={"time-column"} className="font-bold">
                4. Select Time-Stamp Column (Optional):
              </label>
            </div>
            <div className="w-full">
              <SingleColumnSelect
                name="time-column"
                fileId={currentFile}
                currentSelection={timestampColumn}
                onChange={setTimestampColumn}
                allowedTypes={["numeric", "date"]}
                placeholder="Select Time Column"
                help="This column will be used to build time-varying signals. You can change this later too."
              />
            </div>
          </div>
          <div className="flex mt-2 pt-2">
            <div className="w-1/4">
              <label htmlFor={"id-columns"} className="font-bold">
                5. Select ID Columns (Optional):
              </label>
            </div>
            <div className="w-full">
              <MultiColumnSelect
                name="id-columns"
                fileId={currentFile}
                currentSelection={idColumns}
                onChange={setIdColumns}
                allowedTypes={["numeric", "char"]}
                placeholder="Select ID Columns"
                help="Columns which can be used to build features but need to be dropped for building models, e.g., Customer ID. You can change later as well."
              />
            </div>
          </div>
        </>
      )}
      {submitStatus === "completed" && error && <ApiError error={error} />}
      {currentFile && yDefinition?.length >= 1 && treatment?.length >= 1 && (
        <div className="text-center my-4 py-4">
          <div className="flex flex-wrap -mx-3">
            <div className="w-full px-3">
              <button
                className={clsx(
                  "btn px-0",
                  "text-white",
                  "bg-indigo-600 hover:bg-indigo-700",
                  "w-full",
                  "relative",
                  "flex items-center"
                  // submitStatus === "in-progress" && "cursor-not-allowed"
                )}
                disabled={submitStatus === "in-progress"}
                onClick={create}
              >
                {submitStatus === "in-progress" && (
                  <svg
                    className="animate-spin h-5 w-5 mr-3  text-white"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                )}
                {submitStatus === "in-progress"
                  ? "Creating..."
                  : "Create New Project"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateProject;
